/* eslint-disable react/no-danger */
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { Parallax } from 'components';
import DOMPurify from 'isomorphic-dompurify';
import { hexToRGB, getAltTagFromFileName } from 'utils';
import { GatsbyImage } from 'gatsby-plugin-image';
import ReactMutliCarousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { OpenQuote, CloseQuote } from 'assets/svgs/';

const testimonialData = [
  {
    name: 'Hugh McCallum',
    text: "Overall I'm very happy with Connolly Stairs. Great value for money and fantastic stairs. We love the stairs - they look great already and they're not even finished yet.<br />I would also like to compliment the three guys who installed the stairs as well as the plasterer. They have been quick, efficient, friendly and professional. The plasterer was so diligent in cleaning up after himself also - great job all round. Thank you."
  },
  {
    name: 'Carolann Brady',
    text: 'Such lovely and professional group of people and fast. I explained what I needed, they measured up, and when they were ready, it took less than a day to install.'
  },
  {
    name: 'Phillip Coey',
    text: 'Excellent craftmanship. The retrofit of our stairs has exceeded our expectations and is a feature in our home now. The lads were great!'
  },
  {
    name: 'Lisa McGuirk',
    text: "I'm delighted with my new stair rail and bannister. It has transformed the hall, which had an ugly 1960s solid wall going up the stairs. I highly recommend Connolly Stairs. The fitters Gavin, James, Adrian and plasterer Olegas are superb craftsmen, they arrived on time and cleaned up as they went along. Really excellent workmanship. We plan to get new doors at some stage and will definitely go back to Connolly."
  },
  {
    name: 'Anna Fitzgerald',
    text: 'We had a whole new banister/spindles/side panelling done yesterday and I have to say such a pleasure to deal with all staff from start to finish. Naomi was a mindful of information which helped us greatly in all our choosing... And the two guys that fitted the banister were so professional and courteous and did an exceptional job. 👏 Would highly recommend Connolly Stairs.'
  },
  {
    name: 'Gerard McCarthy',
    text: "Can highly recommend Connolly Stairs. Got a new stairs installed by them recently and was very pleased with the final result. Installation staff were punctual and very pleasant to deal with. Tidied up after they were finished. Would certainly use Connolly's again."
  },
  {
    name: 'Tony M',
    text: 'Very nice to deal with from start to finish.<br />Naomi was very helpful and always available to answer questions.Fitters were on time, clean and tidy. I would highly recommend Connolly stairs.'
  }
];

const Testimonials = forwardRef((props, ref) => {
  const { backgroundImage } = useStaticQuery(graphql`
    query {
      backgroundImage: file(relativePath: { eq: "l-shaped-stairs-white-carpet.png" }) {
        name
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
  `);

  const { title } = props;

  return (
    <Container ref={ref} $backgroundColor={props.backgroundColor}>
      <h3>{title || 'Testimonials'}</h3>
      <InnerContainer className="fade-in">
        <Parallax style={{ gridColumn: '1/-1', gridRow: '1/-1' }}>
          <BackgroundImage
            image={backgroundImage.childImageSharp.gatsbyImageData}
            alt={getAltTagFromFileName(backgroundImage.name)}
          />
        </Parallax>
        <ReactMutliCarouselWrapper>
          <div>
            <StyledOpenQuote />
            <StyledReactMutliCarousel
              responsive={{
                desktop: {
                  breakpoint: { max: 10000, min: 1200 },
                  items: 3
                },
                table: {
                  breakpoint: { max: 1199, min: 840 },
                  items: 2
                },
                mobile: {
                  breakpoint: { max: 839, min: 0 },
                  items: 1
                }
              }}
              swipeable
              draggable
              showDots
              arrows={false}
              ssr
              infinite
              autoPlay
              autoPlaySpeed={4000}
              keyBoardControl
              // transitionDuration={500}
              renderDotsOutside>
              {testimonialData.map((testimonial, i) => {
                const { name, text } = testimonial;
                return (
                  <Testimonial key={i}>
                    <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />
                    <h3 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(name) }} />
                  </Testimonial>
                );
              })}
            </StyledReactMutliCarousel>
            <StyledCloseQuote />
          </div>
        </ReactMutliCarouselWrapper>
      </InnerContainer>
    </Container>
  );
});

const Container = styled.section`
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'var(--connolly-grey)'};
  width: 100%;

  > h3 {
    color: var(--connolly-black);
    font-size: 2rem;
    padding: 1.75em 2rem 0.625em;
    text-align: center;

    @media (width >= 768px) {
      padding: 3.125em 0 1.25em;
    }
  }
`;

const Testimonial = styled.div`
  background-color: ${hexToRGB({ color: '#f1f1f1', alpha: 0.9 })};
  border-radius: 0.375rem;
  color: var(--connolly-black);
  height: 100%;
  margin: 0 auto;
  max-width: 340px;
  padding: 3.4375rem 1.9375rem 3.3125rem;
  width: 100%;

  @media (width >= 430px) {
    margin: unset;
    max-width: 22.5rem;
  }

  @media (width >= 1200px) {
    max-width: 20.5rem;
  }

  @media (width >= 1270px) {
    max-width: 22.5rem;
  }

  p {
    font-family: 'Open Sans', 'Segoe UI', Tahoma, sans-serif;
    margin-bottom: 1.6875rem;
  }
`;

const InnerContainer = styled.section`
  align-content: center;
  align-items: center;
  column-gap: 1.25rem;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  height: 574px;
  overflow: hidden;
`;

const BackgroundImage = styled(GatsbyImage)`
  height: 900px;
  position: relative;
  top: -50px;

  @media (width >= 768px) {
    top: -150px;
  }

  @media (width >= 1800px) {
    top: -350px;
  }
`;

const ReactMutliCarouselWrapper = styled.div`
  grid-column: 1/-1;
  grid-row: 1/-1;

  > div {
    position: relative;
    margin: 0 auto;
    max-width: 22rem;

    @media (width >= 430px) {
      max-width: 25.6rem;
    }

    @media (width >= 840px) {
      max-width: 51.25rem;
    }

    @media (width >= 1200px) {
      max-width: 71rem;
    }

    @media (width >= 1270px) {
      max-width: 76.875rem;
    }
  }

  .react-multi-carousel-dot-list {
    bottom: -2.125rem;
    left: 0.4375rem;
  }

  .react-multi-carousel-dot button {
    border: none;
    width: 0.625rem;
    height: 0.625rem;
  }

  .react-multi-carousel-dot--active button {
    background-color: var(--connolly-red);
  }
`;

const StyledReactMutliCarousel = styled(ReactMutliCarousel)`
  position: relative !important;

  @media (width >= 430px) {
    left: 1.5625rem;
  }

  ${({ children }) =>
    children.length < 3 &&
    css`
      .react-multi-carousel-track {
        margin: 0 auto !important;
        transform: translate3d(0, 0, 0) !important;
      }
    `}
`;

const StyledOpenQuote = styled(OpenQuote)`
  left: 1.5rem;
  position: absolute;
  top: -1.1875rem;
  width: 3.1875rem;
  z-index: 1;

  @media (width >= 430px) {
    left: 3.4375rem;
  }
`;

const StyledCloseQuote = styled(CloseQuote)`
  bottom: -1.375rem;
  position: absolute;
  right: 1.5rem;
  width: 3.1875rem;
  z-index: 1;

  @media (width >= 430px) {
    right: 2.8125rem;
  }
`;

export default Testimonials;
